import React, { useEffect, useState } from 'react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, XCircle, Mail, Building, User, Calendar } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface InvitationDetails {
  id: string;
  email: string;
  organization_name: string;
  role: string;
  expires_at: string;
  invited_by_name: string;
}

interface AcceptInvitationResult {
  success: boolean;
  error?: string;
  organization_id?: string;
  role?: string;
}

const AcceptInvitation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [autoAcceptAttempted, setAutoAcceptAttempted] = useState(false);

  // Get token from URL and normalize it (fix spaces that may have been introduced by email clients)
  const rawToken = searchParams.get('token');
  const token = rawToken ? rawToken.replace(/ /g, '+') : null;

  useEffect(() => {
    if (!token) {
      setError('No invitation token provided');
      setLoading(false);
      return;
    }

    fetchInvitationDetails();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps -- fetchInvitationDetails depends on token

  const fetchInvitationDetails = async () => {
    try {
      console.log('Fetching invitation details for token:', token);
      
      const { data, error } = await supabase.rpc('get_invitation_details', {
        token
      });

      console.log('Invitation details response:', { data, error });

      if (error) {
        console.error('Error fetching invitation details:', error);
        throw error;
      }

      if (!data || (Array.isArray(data) && data.length === 0)) {
        setError('Invalid or expired invitation token');
        return;
      }

      const invitationData = Array.isArray(data) ? data[0] : data;
      console.log('Processed invitation data:', invitationData);
      setInvitation(invitationData);
    } catch (err: any) {
      console.error('Error fetching invitation details:', err);
      setError('Failed to load invitation details');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!user || !token) {
      setError('You must be logged in to accept an invitation');
      return;
    }

    // Check if the invitation email matches the logged-in user's email
    if (invitation && invitation.email !== user.email) {
      setError('This invitation was sent to a different email address. Please log in with the correct account.');
      return;
    }

    setAccepting(true);
    setError(null);
    
    try {
      console.log('Accepting invitation with token:', token, 'and user ID:', user.id);
      
      const { data, error } = await supabase.rpc('accept_invitation', {
        invitation_token: token,
        user_id: user.id
      });

      console.log('Accept invitation response:', { data, error });

      if (error) {
        console.error('Error accepting invitation:', error);
        throw error;
      }

      // Properly handle the Supabase RPC response
      const result = data as unknown as AcceptInvitationResult;
      
      if (result && result.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        setError(result?.error || 'Failed to accept invitation');
      }
    } catch (err: any) {
      console.error('Error accepting invitation:', err);
      if (err.message.includes('Invalid or expired invitation')) {
        setError('This invitation has expired or has already been used.');
      } else if (err.message.includes('already a member')) {
        setError('You are already a member of this organization.');
      } else {
        setError('Failed to accept invitation. Please try again or contact your administrator.');
      }
    } finally {
      setAccepting(false);
    }
  };

  useEffect(() => {
    if (autoAcceptAttempted || accepting || success) {
      return;
    }

    if (!user || !invitation) {
      return;
    }

    const isExpired = new Date(invitation.expires_at) < new Date();
    const emailMatches = user.email === invitation.email;

    if (isExpired || !emailMatches) {
      return;
    }

    setAutoAcceptAttempted(true);
    handleAcceptInvitation();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleAcceptInvitation reads latest state via closure; re-running it on every render would cause repeated RPC calls
  }, [user, invitation, accepting, success, autoAcceptAttempted]);

  if (loading) {
    return <LoadingSpinner size="fullscreen" />;
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <CardTitle className="text-green-700">Invitation Accepted!</CardTitle>
            <CardDescription>
              Welcome to the organization. You're being redirected to the dashboard...
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <CardTitle className="text-red-700">Invalid Invitation</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => navigate('/auth')} 
              className="w-full"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Loading...</CardTitle>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const isExpired = new Date(invitation.expires_at) < new Date();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <Mail className="w-16 h-16 text-blue-500 mx-auto mb-4" />
          <CardTitle>Organization Invitation</CardTitle>
          <CardDescription>
            You've been invited to join an organization
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Building className="w-5 h-5 text-gray-600" />
              <div>
                <p className="font-medium">{invitation.organization_name}</p>
                <p className="text-sm text-gray-600">Organization</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <User className="w-5 h-5 text-gray-600" />
              <div>
                <p className="font-medium">
                  {invitation.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </p>
                <p className="text-sm text-gray-600">Your Role</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Mail className="w-5 h-5 text-gray-600" />
              <div>
                <p className="font-medium">{invitation.email}</p>
                <p className="text-sm text-gray-600">Invited Email</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Calendar className="w-5 h-5 text-gray-600" />
              <div>
                <p className="font-medium">
                  {new Date(invitation.expires_at).toLocaleDateString()}
                </p>
                <p className="text-sm text-gray-600">Expires On</p>
              </div>
            </div>
          </div>

          {isExpired && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>
                This invitation has expired. Please contact your administrator for a new invitation.
              </AlertDescription>
            </Alert>
          )}

          {!user && (
            <Alert>
              <AlertDescription>
                You need to be logged in to accept this invitation. Please log in first.
              </AlertDescription>
            </Alert>
          )}

          {user && invitation && user.email !== invitation.email && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>
                This invitation was sent to {invitation.email}, but you're logged in as {user.email}. 
                Please log in with the correct account.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                if (!user) {
                  // Preserve the invitation token in the redirect
                  navigate(`/auth?redirect=${encodeURIComponent(`/accept-invite?token=${encodeURIComponent(rawToken || '')}`)}`);
                } else {
                  navigate('/');
                }
              }}
              className="flex-1"
            >
              {user ? 'Cancel' : 'Login'}
            </Button>
            <Button
              onClick={handleAcceptInvitation}
              disabled={accepting || isExpired || !user || (user && invitation && user.email !== invitation.email)}
              className="flex-1"
            >
              {accepting ? 'Accepting...' : 'Accept Invitation'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptInvitation;
